// Libraries
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";

// Styles
import {
  CustomTextFieldProps,
  getMenuItemStateColors,
  getPopoverPaperSx,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

// Components
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";
import { CaretDownIcon } from "src/assets/icons/CaretDownIcon";

type CustomAutocompleteType = {
  id?: string;
  data: Array<{ displayName: string; value: string }>;
  value: { displayName: string; value: string };
  onChange: (params: { displayName: string; value: string }) => void;
  maxHeight?: string;
  defaultIsListVisible?: boolean;
  parentOpen?: boolean;
  isDisabled?: boolean;
};

const CustomAutocomplete = ({
  id,
  data = [],
  value,
  onChange,
  maxHeight = "200px",
  defaultIsListVisible = false,
  parentOpen = false,
  isDisabled = false,
}: CustomAutocompleteType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, inputBorder } = getSurfaceTokens(theme);
  const { selected: selectedItemColor, hover: hoverItemColor } =
    getMenuItemStateColors(theme);
  const [valueOfCustomAutocomplete, setValueOfCustomAutocomplete] =
    useState("");
  const [isListVisible, setIsListVisible] = useState(defaultIsListVisible);
  const [isFocused, setIsFocused] = useState(false);
  const [mouseIsOverList, setMouseIsOverList] = useState(false);

  const listRef = useRef(null);
  const stateRef = useRef<string | null>(null);
  const textFieldRef = useRef<HTMLInputElement | null>(null);
  const searchTermRef = useRef("");

  const filteredItems = data.filter((item) => {
    const term = searchTermRef.current.toLowerCase();
    return t(item?.displayName).toLowerCase().includes(term);
  });

  useEffect(() => {
    const val =
      value?.displayName && value.displayName !== ""
        ? t(value.displayName)
        : searchTermRef.current;

    setValueOfCustomAutocomplete(val);
  }, [value, setValueOfCustomAutocomplete, t]);

  const handleItemClick = useCallback(
    (item: { displayName: string; value: string }) => {
      searchTermRef.current = "";
      stateRef.current = item?.displayName;
      onChange(item);
      setIsListVisible(false);
      setIsFocused(false);
    },
    [onChange]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      searchTermRef.current = value;
      onChange({ displayName: "", value: "" });
      stateRef.current = value;
      setIsListVisible(true);
      setIsFocused(true);
      setValueOfCustomAutocomplete(value); // Update only for display purposes
    },
    [onChange]
  );

  const handleInputFocus = useCallback(() => {
    setIsListVisible(true);
    setIsFocused(true);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setMouseIsOverList(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMouseIsOverList(false);
  }, []);

  const handleInputBlur = useCallback(() => {
    if (stateRef.current) {
      const corresponding = data.find(
        (datum) => datum.displayName === stateRef.current
      );

      onChange(
        corresponding ?? {
          displayName: stateRef.current,
          value: stateRef.current,
        }
      );
    }

    setTimeout(() => {
      if (!mouseIsOverList) {
        setIsListVisible(false);
        setIsFocused(false);
      }
    }, 100);
  }, [data, onChange, mouseIsOverList]);

  const handleMouseDown = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
      item: { displayName: string; value: string }
    ) => {
      e.preventDefault();
      handleItemClick(item);
    },
    [handleItemClick]
  );

  useEffect(() => {
    if (parentOpen && defaultIsListVisible && textFieldRef?.current) {
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentOpen]);

  return (
    <div>
      <div>
        <TextField
          id={id}
          sx={{
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "#E2772E",
              },
          }}
          disabled={isDisabled}
          value={valueOfCustomAutocomplete}
          onChange={handleChange}
          placeholder={t("Select a value")}
          InputProps={{
            sx: {
              ...CustomTextFieldProps(theme),
              padding: "0 !important",
              "& .MuiAutocomplete-input": {
                padding: "0 !important",
              },
              ".MuiInputBase-input": { padding: "0px 16px" },
              ".MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${inputBorder}`,
              },
            },
            endAdornment: (
              <InputAdornment position="start">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    width: "16px",
                    height: "16px",
                  }}
                  onClick={() => {
                    if (isListVisible && isFocused) {
                      handleInputBlur();
                    } else {
                      handleInputFocus();
                      // set the focus so the list closes when clicking outside of it
                      textFieldRef.current?.focus();
                    }
                  }}
                >
                  <CaretDownIcon />
                </Box>
              </InputAdornment>
            ),
          }}
          inputProps={{
            maxLength: 32,
            sx: {
              fontSize: "14px",
              "&::placeholder": {
                color: inputBorder,
                opacity: 1,
              },
            },
          }}
          inputRef={textFieldRef}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          fullWidth
          autoComplete="off"
        />
      </div>
      {isListVisible && (
        <Box
          data-testid="autocomplete-list"
          style={{
            ...getPopoverPaperSx(theme),
            maxHeight,
            overflowY: "auto",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
            borderRadius: "0px 0px 4px 4px",
            color: onSurface,
          }}
          ref={listRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {filteredItems?.length > 0 ? (
            filteredItems.map((item, index) => (
              <Box
                key={index}
                data-testid={`autocomplete-item-${index}`}
                onMouseDown={(e) => handleMouseDown(e, item)}
                aria-label={item?.value}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: "16px",
                  fontSize: "14px",
                  minHeight: "36px",
                  cursor: "pointer",
                  backgroundColor:
                    item.value === value.value
                      ? `${selectedItemColor} !important`
                      : "transparent",

                  "&:hover": {
                    backgroundColor:
                      item.value === value.value
                        ? `${selectedItemColor} !important`
                        : `${hoverItemColor} !important`,
                  },
                }}
              >
                {item?.displayName}
              </Box>
            ))
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: "16px",
                minHeight: "52px",
              }}
            >
              {t("No results found.")}
            </Box>
          )}
        </Box>
      )}
    </div>
  );
};

export default CustomAutocomplete;
